import Dexie, { type EntityTable } from "dexie";
import type { Archive, ChatMessage } from "../domain/types";
export interface ArchiveRow {
  name: string;
  data: Archive;
  updatedAt: number;
  revision: number;
}
type ArchiveCoreV5 = Omit<Archive, "messages"> & { activeMessageIds: string[] };
interface PersistedArchiveRowV5 {
  archiveId: string;
  name: string;
  data: ArchiveCoreV5;
  updatedAt: number;
  revision: number;
  schemaVersion: 5;
}
export interface SettingRow {
  key: string;
  value: unknown;
  updatedAt: number;
}
export interface VectorRow {
  id: string;
  scope: "archive" | "global";
  archiveName?: string;
  content: string;
  vector: number[];
  metadata: Record<string, unknown>;
  createdAt: number;
}
export interface PendingTurnRow {
  id: string;
  archiveName: string;
  input: string;
  startedAt: number;
  phase:
    | "prepared"
    | "directing"
    | "retrieving"
    | "main-streaming"
    | "main-complete"
    | "committing"
    | "settling"
    | "evolving"
    | "complete"
    | "failed"
    | "aborted";
  assistantText?: string;
  updatedAt?: number;
  error?: string;
}
export interface StoredChatMessage extends ChatMessage {
  archiveId?: string;
  archiveName: string;
  turnId?: string;
}
export interface TurnCheckpointRow {
  id: string;
  archiveName: string;
  turnId: string;
  createdAt: number;
  snapshot: Archive;
}
export interface LlmDebugLogRow {
  id: string;
  archiveName?: string;
  turnId?: string;
  featureId: string;
  routeId: string;
  host: string;
  model: string;
  attempt: number;
  status: "ok" | "error" | "aborted";
  startedAt: number;
  durationMs: number;
  requestPreview: string;
  responsePreview: string;
  truncated: boolean;
  error?: string;
  usage?: Record<string, number>;
}
export interface TurnReplayContextRow {
  id: string;
  archiveName: string;
  turnId: string;
  input: string;
  createdAt: number;
  context: { rag: string; memory: string; guidance: string };
}
export type TurnTraceStage = PendingTurnRow["phase"];
export interface TurnTraceStageRow {
  stage: TurnTraceStage;
  status: "running" | "ok" | "error" | "aborted";
  startedAt: number;
  finishedAt?: number;
  error?: string;
}
export interface TurnTraceRow {
  id: string;
  archiveName: string;
  inputLength: number;
  startedAt: number;
  updatedAt: number;
  status: "running" | "ok" | "error" | "aborted";
  stages: TurnTraceStageRow[];
}
class GameDB extends Dexie {
  archives!: EntityTable<PersistedArchiveRowV5, "archiveId">;
  settings!: EntityTable<SettingRow, "key">;
  vectors!: EntityTable<VectorRow, "id">;
  archiveChatMessages!: EntityTable<StoredChatMessage, "id">;
  pendingMainTurns!: EntityTable<PendingTurnRow, "id">;
  turnCheckpoints!: EntityTable<TurnCheckpointRow, "id">;
  llmDebugLogs!: EntityTable<LlmDebugLogRow, "id">;
  turnReplayContexts!: EntityTable<TurnReplayContextRow, "id">;
  turnTraces!: EntityTable<TurnTraceRow, "id">;
  constructor() {
    super("MH_RP_Game_V2");
    this.version(1).stores({
      archives: "archiveId,&name,updatedAt,revision,schemaVersion",
      settings: "key,updatedAt",
      vectors: "id,[scope+archiveName],scope,archiveName,createdAt",
      archiveChatMessages:
        "id,[archiveId+createdAt],[archiveName+createdAt],archiveId,archiveName,createdAt,turnId",
      pendingMainTurns: "id,archiveName,startedAt",
    });
    this.version(2).stores({
      archives: "archiveId,&name,updatedAt,revision,schemaVersion",
      settings: "key,updatedAt",
      vectors: "id,[scope+archiveName],scope,archiveName,createdAt",
      archiveChatMessages:
        "id,[archiveId+createdAt],[archiveName+createdAt],archiveId,archiveName,createdAt,turnId",
      pendingMainTurns: "id,archiveName,startedAt",
      turnCheckpoints:
        "id,[archiveName+createdAt],archiveName,turnId,createdAt",
      llmDebugLogs:
        "id,[archiveName+startedAt],archiveName,featureId,status,startedAt",
    });
    this.version(3).stores({
      archives: "archiveId,&name,updatedAt,revision,schemaVersion",
      settings: "key,updatedAt",
      vectors: "id,[scope+archiveName],scope,archiveName,createdAt",
      archiveChatMessages:
        "id,[archiveId+createdAt],[archiveName+createdAt],archiveId,archiveName,createdAt,turnId",
      pendingMainTurns: "id,archiveName,startedAt",
      turnCheckpoints:
        "id,[archiveName+createdAt],archiveName,turnId,createdAt",
      llmDebugLogs:
        "id,[archiveName+startedAt],archiveName,featureId,status,startedAt",
      turnReplayContexts:
        "id,[archiveName+createdAt],archiveName,turnId,createdAt",
    });
    this.version(4).stores({
      archives: "archiveId,&name,updatedAt,revision,schemaVersion",
      settings: "key,updatedAt",
      vectors: "id,[scope+archiveName],scope,archiveName,createdAt",
      archiveChatMessages:
        "id,[archiveId+createdAt],[archiveName+createdAt],archiveId,archiveName,createdAt,turnId",
      pendingMainTurns: "id,archiveName,startedAt",
      turnCheckpoints:
        "id,[archiveName+createdAt],archiveName,turnId,createdAt",
      llmDebugLogs:
        "id,[archiveName+startedAt],archiveName,featureId,status,startedAt",
      turnReplayContexts:
        "id,[archiveName+createdAt],archiveName,turnId,createdAt",
      turnTraces: "id,[archiveName+startedAt],archiveName,status,startedAt",
    });
  }
}
export const db = new GameDB();
const knownRevisionByArchiveId = new Map<string, number>();
let legacyMigrationPromise: Promise<void> | null = null;

class LegacyGameDB extends Dexie {
  archives!: EntityTable<ArchiveRow, "name">;
  settings!: EntityTable<SettingRow, "key">;
  vectors!: EntityTable<VectorRow, "id">;
  archiveChatMessages!: EntityTable<StoredChatMessage, "id">;
  pendingMainTurns!: EntityTable<PendingTurnRow, "id">;
  turnCheckpoints!: EntityTable<TurnCheckpointRow, "id">;
  llmDebugLogs!: EntityTable<LlmDebugLogRow, "id">;
  turnReplayContexts!: EntityTable<TurnReplayContextRow, "id">;
  constructor() {
    super("MH_RP_Game_V1");
    this.version(3).stores({
      archives: "name,updatedAt,revision",
      settings: "key,updatedAt",
      vectors: "id,[scope+archiveName],scope,archiveName,createdAt",
      archiveChatMessages: "id,[archiveName+createdAt],archiveName,createdAt,turnId",
      pendingMainTurns: "id,archiveName,startedAt",
      turnCheckpoints: "id,[archiveName+createdAt],archiveName,turnId,createdAt",
      llmDebugLogs: "id,[archiveName+startedAt],archiveName,featureId,status,startedAt",
      turnReplayContexts: "id,[archiveName+createdAt],archiveName,turnId,createdAt",
    });
  }
}

function archiveCore(archive: Archive): ArchiveCoreV5 {
  const { messages, ...state } = structuredClone(archive);
  return { ...state, activeMessageIds: messages.map((message) => message.id) };
}

async function hydrateArchive(row: PersistedArchiveRowV5): Promise<Archive> {
  const stored = await db.archiveChatMessages.where("archiveId").equals(row.archiveId).toArray();
  const byId = new Map(stored.map((message) => [message.id, message]));
  const messages = row.data.activeMessageIds.flatMap((id) => {
    const message = byId.get(id);
    if (!message) return [];
    const { archiveId: _archiveId, archiveName: _archiveName, ...chat } = message;
    return [chat];
  });
  const { activeMessageIds: _activeMessageIds, ...core } = structuredClone(row.data);
  knownRevisionByArchiveId.set(row.archiveId, row.revision);
  return { ...core, messages };
}

async function persistArchiveV5(archive: Archive, expectedRevision?: number) {
  const now = Date.now();
  await db.transaction("rw", db.archives, db.archiveChatMessages, async () => {
    const current = (await db.archives.get(archive.archiveId)) ??
      (await db.archives.where("name").equals(archive.name).first());
    const knownRevision = expectedRevision ?? knownRevisionByArchiveId.get(archive.archiveId);
    if (knownRevision !== undefined && (current?.revision ?? 0) !== knownRevision)
      throw new Error("存档已被另一写入更新，请重新加载");
    if (current && current.archiveId !== archive.archiveId)
      throw new Error(`存档名称「${archive.name}」已被使用`);
    const data = structuredClone(archive);
    data.meta.updatedAt = now;
    const revision = (current?.revision ?? 0) + 1;
    await db.archives.put({
      archiveId: data.archiveId,
      name: data.name,
      data: archiveCore(data),
      updatedAt: now,
      revision,
      schemaVersion: 5,
    });
    if (data.messages.length) await db.archiveChatMessages.bulkPut(data.messages.map((message) => ({ ...message, archiveId: data.archiveId, archiveName: data.name })));
    knownRevisionByArchiveId.set(data.archiveId, revision);
  });
  archive.meta.updatedAt = now;
}

async function ensureLegacyMigration() {
  legacyMigrationPromise ??= (async () => {
    if ((await db.settings.get("migration:v1-copy"))?.value === true) return;
    if (!(await Dexie.exists("MH_RP_Game_V1"))) {
      await db.settings.put({ key: "migration:v1-copy", value: true, updatedAt: Date.now() });
      return;
    }
    const legacy = new LegacyGameDB();
    try {
      const legacyArchives = await legacy.archives.toArray();
      const failures: string[] = [];
      for (const row of legacyArchives) {
        try { await persistArchiveV5(row.data); }
        catch (error) { failures.push(`${row.name}: ${error instanceof Error ? error.message : String(error)}`); }
      }
      const [settings, vectors, pendingTurns, checkpoints, logs, replayContexts] = await Promise.all([
        legacy.settings.toArray(), legacy.vectors.toArray(), legacy.pendingMainTurns.toArray(), legacy.turnCheckpoints.toArray(), legacy.llmDebugLogs.toArray(), legacy.turnReplayContexts.toArray(),
      ]);
      await db.transaction("rw", [db.settings, db.vectors, db.pendingMainTurns, db.turnCheckpoints, db.llmDebugLogs, db.turnReplayContexts], async () => {
        if (settings.length) await db.settings.bulkPut(settings);
        if (vectors.length) await db.vectors.bulkPut(vectors);
        if (pendingTurns.length) await db.pendingMainTurns.bulkPut(pendingTurns);
        if (checkpoints.length) await db.turnCheckpoints.bulkPut(checkpoints);
        if (logs.length) await db.llmDebugLogs.bulkPut(logs);
        if (replayContexts.length) await db.turnReplayContexts.bulkPut(replayContexts);
        await db.settings.put({ key: "migration:v1-copy-report", value: { copied: legacyArchives.length - failures.length, failures }, updatedAt: Date.now() });
        await db.settings.put({ key: "migration:v1-copy", value: true, updatedAt: Date.now() });
      });
    } finally { legacy.close(); }
  })().catch((error) => { legacyMigrationPromise = null; throw error; });
  await legacyMigrationPromise;
}
export function encodeEmbedding(vector: number[]) {
  const values = new Float32Array(vector);
  return Array.from(values);
}
export async function saveArchive(archive: Archive, expectedRevision?: number) {
  await ensureLegacyMigration();
  await persistArchiveV5(archive, expectedRevision);
}
export async function loadArchive(name: string) {
  await ensureLegacyMigration();
  const row = await db.archives.where("name").equals(name).first();
  return row ? hydrateArchive(row) : null;
}
export async function listArchives() {
  await ensureLegacyMigration();
  const rows = await db.archives.orderBy("updatedAt").reverse().toArray();
  return Promise.all(rows.map(async (row): Promise<ArchiveRow> => ({ name: row.name, data: await hydrateArchive(row), updatedAt: row.updatedAt, revision: row.revision })));
}
export async function deleteArchive(name: string) {
  await ensureLegacyMigration();
  const archiveRow = await db.archives.where("name").equals(name).first();
  if (!archiveRow) return;
  await db.transaction(
    "rw",
    [
      db.archives,
      db.archiveChatMessages,
      db.pendingMainTurns,
      db.vectors,
      db.turnCheckpoints,
      db.llmDebugLogs,
      db.turnReplayContexts,
      db.turnTraces,
    ],
    async () => {
      await db.archives.delete(archiveRow.archiveId);
      await db.archiveChatMessages.where("archiveId").equals(archiveRow.archiveId).delete();
      await db.pendingMainTurns.where("archiveName").equals(name).delete();
      await db.vectors.where("archiveName").equals(name).delete();
      await db.turnCheckpoints.where("archiveName").equals(name).delete();
      await db.llmDebugLogs.where("archiveName").equals(name).delete();
      await db.turnReplayContexts.where("archiveName").equals(name).delete();
      await db.turnTraces.where("archiveName").equals(name).delete();
    },
  );
  knownRevisionByArchiveId.delete(archiveRow.archiveId);
}

export async function putTurnReplayContext(row: TurnReplayContextRow) {
  await db.turnReplayContexts.put(row);
  const rows = await db.turnReplayContexts
    .where("archiveName")
    .equals(row.archiveName)
    .sortBy("createdAt");
  if (rows.length > 50)
    await db.turnReplayContexts.bulkDelete(
      rows.slice(0, rows.length - 50).map((item) => item.id),
    );
}

export async function listTurnReplayContexts(archiveName: string) {
  return db.turnReplayContexts
    .where("archiveName")
    .equals(archiveName)
    .sortBy("createdAt");
}

export async function createTurnCheckpoint(
  archive: Archive,
  turnId: string,
): Promise<TurnCheckpointRow> {
  const row: TurnCheckpointRow = {
    id: `checkpoint:${archive.archiveId}:${turnId}`,
    archiveName: archive.name,
    turnId,
    createdAt: Date.now(),
    snapshot: structuredClone(archive),
  };
  await db.turnCheckpoints.put(row);
  return row;
}

export async function getTurnCheckpoint(id: string) {
  return db.turnCheckpoints.get(id);
}

export async function listTurnCheckpoints(archiveName: string) {
  return db.turnCheckpoints
    .where("archiveName")
    .equals(archiveName)
    .sortBy("createdAt");
}

export async function restoreTurnCheckpoint(id: string): Promise<Archive> {
  const checkpoint = await db.turnCheckpoints.get(id);
  if (!checkpoint) throw new Error("该消息没有可用的回溯保存点");
  const snapshot = structuredClone(checkpoint.snapshot);
  await db.transaction(
    "rw",
    [
      db.archives,
      db.archiveChatMessages,
      db.pendingMainTurns,
      db.turnCheckpoints,
      db.vectors,
      db.turnReplayContexts,
      db.turnTraces,
    ],
    async () => {
      const current = (await db.archives.get(snapshot.archiveId)) ??
        (await db.archives.where("name").equals(checkpoint.archiveName).first());
      const updatedAt = Date.now();
      snapshot.meta.updatedAt = updatedAt;
      await db.archives.put({
        archiveId: snapshot.archiveId,
        name: snapshot.name,
        data: archiveCore(snapshot),
        updatedAt,
        revision: (current?.revision ?? 0) + 1,
        schemaVersion: 5,
      });
      if (snapshot.messages.length)
        await db.archiveChatMessages.bulkPut(
          snapshot.messages.map((message) => ({
            ...message,
            archiveId: snapshot.archiveId,
            archiveName: snapshot.name,
          })),
        );
      await db.pendingMainTurns
        .where("archiveName")
        .equals(checkpoint.archiveName)
        .delete();
      const later = await db.turnCheckpoints
        .where("archiveName")
        .equals(checkpoint.archiveName)
        .filter((row) => row.createdAt >= checkpoint.createdAt)
        .primaryKeys();
      await db.turnCheckpoints.bulkDelete(later);
      await db.vectors
        .where("archiveName")
        .equals(checkpoint.archiveName)
        .filter((row) => row.createdAt >= checkpoint.createdAt)
        .delete();
      await db.turnReplayContexts
        .where("archiveName")
        .equals(checkpoint.archiveName)
        .filter((row) => row.createdAt >= checkpoint.createdAt)
        .delete();
      await db.turnTraces
        .where("archiveName")
        .equals(checkpoint.archiveName)
        .filter((row) => row.startedAt >= checkpoint.createdAt)
        .delete();
    },
  );
  knownRevisionByArchiveId.set(snapshot.archiveId, ((await db.archives.get(snapshot.archiveId))?.revision ?? 0));
  return snapshot;
}

const DEBUG_PREVIEW_LIMIT = 64 * 1024;
export function truncateDebugText(value: string) {
  return value.length <= DEBUG_PREVIEW_LIMIT
    ? { value, truncated: false }
    : { value: value.slice(0, DEBUG_PREVIEW_LIMIT), truncated: true };
}
export async function putLlmDebugLog(row: LlmDebugLogRow) {
  await db.llmDebugLogs.put(row);
  const count = await db.llmDebugLogs.count();
  if (count > 200) {
    const old = await db.llmDebugLogs
      .orderBy("startedAt")
      .limit(count - 200)
      .primaryKeys();
    await db.llmDebugLogs.bulkDelete(old);
  }
}
export async function listLlmDebugLogs(archiveName?: string) {
  return archiveName
    ? db.llmDebugLogs
        .where("archiveName")
        .equals(archiveName)
        .reverse()
        .sortBy("startedAt")
    : db.llmDebugLogs.orderBy("startedAt").reverse().toArray();
}
export async function putSetting(key: string, value: unknown) {
  await db.settings.put({ key, value, updatedAt: Date.now() });
}
export async function getSetting<T>(key: string, fallback: T): Promise<T> {
  return ((await db.settings.get(key))?.value as T) ?? fallback;
}
export async function beginPendingTurn(row: PendingTurnRow) {
  await db.pendingMainTurns.put({ ...row, updatedAt: Date.now() });
}
export async function updatePendingTurn(
  id: string,
  patch: Partial<Omit<PendingTurnRow, "id" | "archiveName">>,
) {
  const current = await db.pendingMainTurns.get(id);
  if (!current) throw new Error(`pending turn 不存在：${id}`);
  await db.pendingMainTurns.put({ ...current, ...patch, updatedAt: Date.now() });
}
export async function finishPendingTurn(id: string) {
  await db.pendingMainTurns.delete(id);
}
export async function listRecoverableTurns(archiveName: string) {
  return db.pendingMainTurns
    .where("archiveName")
    .equals(archiveName)
    .sortBy("startedAt");
}

export async function beginTurnTrace(id: string, archiveName: string, inputLength: number) {
  const now = Date.now();
  await db.turnTraces.put({ id, archiveName, inputLength, startedAt: now, updatedAt: now, status: "running", stages: [] });
}
export async function enterTurnTraceStage(id: string, stage: TurnTraceStage) {
  const trace = await db.turnTraces.get(id);
  if (!trace) return;
  const now = Date.now();
  const stages = trace.stages.map((item) => item.status === "running" ? { ...item, status: "ok" as const, finishedAt: now } : item);
  stages.push({ stage, status: "running", startedAt: now });
  await db.turnTraces.put({ ...trace, updatedAt: now, stages });
}
export async function finishTurnTrace(id: string, status: "ok" | "error" | "aborted", error?: string) {
  const trace = await db.turnTraces.get(id);
  if (!trace) return;
  const now = Date.now();
  const stages = trace.stages.map((item) => item.status === "running" ? { ...item, status, finishedAt: now, error } : item);
  await db.turnTraces.put({ ...trace, updatedAt: now, status, stages });
  const rows = await db.turnTraces.orderBy("startedAt").toArray();
  if (rows.length > 100) await db.turnTraces.bulkDelete(rows.slice(0, rows.length - 100).map((row) => row.id));
}
export async function listTurnTraces(archiveName?: string) {
  return archiveName
    ? db.turnTraces.where("archiveName").equals(archiveName).reverse().sortBy("startedAt")
    : db.turnTraces.orderBy("startedAt").reverse().toArray();
}

export interface ArchiveHealthReport {
  status: "healthy" | "warning" | "error";
  checkedAt: number;
  archiveCount: number;
  missingMessageIds: Array<{ archiveId: string; messageId: string }>;
  pendingTurns: PendingTurnRow[];
  legacyMigration: unknown;
}

export async function inspectArchiveHealth(): Promise<ArchiveHealthReport> {
  await ensureLegacyMigration();
  const [archives, messages, pendingTurns, legacyMigration] = await Promise.all([
    db.archives.toArray(),
    db.archiveChatMessages.toArray(),
    db.pendingMainTurns.toArray(),
    getSetting("migration:v1-copy-report", null as unknown),
  ]);
  const ids = new Set(messages.map((message) => `${message.archiveId}:${message.id}`));
  const missingMessageIds = archives.flatMap((archive) => archive.data.activeMessageIds.filter((messageId) => !ids.has(`${archive.archiveId}:${messageId}`)).map((messageId) => ({ archiveId: archive.archiveId, messageId })));
  return {
    status: missingMessageIds.length ? "error" : pendingTurns.length ? "warning" : "healthy",
    checkedAt: Date.now(),
    archiveCount: archives.length,
    missingMessageIds,
    pendingTurns,
    legacyMigration,
  };
}
